import { JSONContent } from "@tiptap/core";
import { TotalEffortType } from "app/types";
import { ITicketDescription } from "./containers/Boards/containers/Tickets/types";
import { IEffortActual } from "./containers/Ticket/types";
import { AttachmentType, JsonDocType } from "./types";

type EffortUnit = "week" | "day" | "hour" | "minute" | "second";

type EffortUnitAliases = {
  [key: string]: EffortUnit;
};

export interface EffortParserOptions {
  minutesPerHour?: number;
  hoursPerDay?: number;
  daysPerWeek?: number;
  secondsPerMinute?: number;
  allowColonFormat?: boolean;
  fallbackUnit?: EffortUnit;
}

export interface ParsedEffortPart {
  raw: string;
  value: number;
  unit: EffortUnit;
  minutes: number;
}

export interface ParsedEffortResult {
  input: string;
  totalMinutes: number;
  hours: number;
  minutes: number;
  formatted: string;
  isValid: boolean;
  parts: ParsedEffortPart[];
}

const EFFORT_UNIT_ALIASES: EffortUnitAliases = {
  w: "week",
  week: "week",
  weeks: "week",
  d: "day",
  day: "day",
  days: "day",
  h: "hour",
  hr: "hour",
  hrs: "hour",
  hour: "hour",
  hours: "hour",
  m: "minute",
  min: "minute",
  mins: "minute",
  minute: "minute",
  minutes: "minute",
  s: "second",
  sec: "second",
  secs: "second",
  second: "second",
  seconds: "second",
};

const DURATION_TOKEN_REGEX = /(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/g;

const COLON_DURATION_REGEX = /(^|\D)(\d{1,3})\s*:\s*(\d{1,2})(?=\D|$)/g;

const DEFAULT_EFFORT_OPTIONS: Required<
  Omit<EffortParserOptions, "fallbackUnit">
> = {
  minutesPerHour: 60,
  hoursPerDay: 8,
  daysPerWeek: 5,
  secondsPerMinute: 60,
  allowColonFormat: true,
};

const EFFORT_UNIT_ALIAS_KEYS = Object.keys(EFFORT_UNIT_ALIASES).sort(
  (a, b) => b.length - a.length,
);

const getNormalizedEffortUnit = (rawUnitToken: string) => {
  for (const alias of EFFORT_UNIT_ALIAS_KEYS) {
    if (rawUnitToken.startsWith(alias)) {
      return EFFORT_UNIT_ALIASES[alias];
    }
  }

  return undefined;
};

const pluralizeUnit = (value: number, singular: string, plural: string) => {
  return `${value} ${value === 1 ? singular : plural}`;
};

const getPartInMinutes = (
  value: number,
  unit: EffortUnit,
  options: Required<Omit<EffortParserOptions, "fallbackUnit">>,
) => {
  switch (unit) {
    case "week":
      return (
        value *
        options.daysPerWeek *
        options.hoursPerDay *
        options.minutesPerHour
      );
    case "day":
      return value * options.hoursPerDay * options.minutesPerHour;
    case "hour":
      return value * options.minutesPerHour;
    case "minute":
      return value;
    case "second":
      return value / options.secondsPerMinute;
    default:
      return value;
  }
};

export const formatEffortFromMinutes = (
  totalMinutes: number,
  options: EffortParserOptions = {},
) => {
  const mergedOptions = {
    ...DEFAULT_EFFORT_OPTIONS,
    ...options,
  };

  const safeMinutes = Math.round(totalMinutes);

  if (!Number.isFinite(safeMinutes) || safeMinutes <= 0) {
    return "";
  }

  const minutesPerHour = mergedOptions.minutesPerHour;

  const hours = Math.floor(safeMinutes / minutesPerHour);
  const minutes = safeMinutes % minutesPerHour;

  const parts: string[] = [];

  if (hours > 0) parts.push(pluralizeUnit(hours, "hour", "hours"));
  if (minutes > 0) parts.push(pluralizeUnit(minutes, "minute", "minutes"));

  return parts.join(" ");
};

export const parseEffortInput = (
  input: string,
  options: EffortParserOptions = {},
): ParsedEffortResult => {
  const mergedOptions = {
    ...DEFAULT_EFFORT_OPTIONS,
    ...options,
  };

  if (!input?.trim()) {
    return {
      input,
      totalMinutes: 0,
      hours: 0,
      minutes: 0,
      formatted: "",
      isValid: false,
      parts: [],
    };
  }

  const parts: ParsedEffortPart[] = [];
  let totalMinutes = 0;
  let totalRawSeconds = 0;

  const durationRegex = new RegExp(
    DURATION_TOKEN_REGEX.source,
    DURATION_TOKEN_REGEX.flags,
  );
  const colonDurationRegex = new RegExp(
    COLON_DURATION_REGEX.source,
    COLON_DURATION_REGEX.flags,
  );
  const fallbackNumericRegex = /(?<![a-zA-Z])\d+(?:\.\d+)?(?![a-zA-Z])/g;

  let match: RegExpExecArray | null;

  while ((match = durationRegex.exec(input)) !== null) {
    const rawValue = match[1];
    const rawUnit = match[2]?.toLowerCase() || "";
    const normalizedUnit = getNormalizedEffortUnit(rawUnit);

    if (!rawValue || !normalizedUnit) {
      continue;
    }

    const value = Number.parseFloat(rawValue);

    if (!Number.isFinite(value) || value <= 0) {
      continue;
    }

    const minutes = getPartInMinutes(value, normalizedUnit, mergedOptions);

    parts.push({
      raw: match[0],
      value,
      unit: normalizedUnit,
      minutes,
    });

    if (normalizedUnit === "second") {
      totalRawSeconds += value;
    } else {
      totalMinutes += minutes;
    }
  }

  if (parts.length === 0 && mergedOptions.allowColonFormat) {
    while ((match = colonDurationRegex.exec(input)) !== null) {
      const hours = Number.parseInt(match[2] || "", 10);
      const minutes = Number.parseInt(match[3] || "", 10);

      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        continue;
      }

      if (hours < 0 || minutes < 0) {
        continue;
      }

      const parsedMinutes =
        getPartInMinutes(hours, "hour", mergedOptions) +
        getPartInMinutes(minutes, "minute", mergedOptions);

      parts.push({
        raw: `${hours}:${minutes}`,
        value: hours,
        unit: "hour",
        minutes: parsedMinutes,
      });

      totalMinutes += parsedMinutes;
    }
  }

  if (parts.length === 0) {
    const fallbackUnit = options.fallbackUnit ?? "hour";
    while ((match = fallbackNumericRegex.exec(input)) !== null) {
      const value = Number.parseFloat(match[0]);

      if (!Number.isFinite(value) || value <= 0) {
        continue;
      }

      const minutes = getPartInMinutes(value, fallbackUnit, mergedOptions);

      parts.push({
        raw: match[0],
        value,
        unit: fallbackUnit,
        minutes,
      });

      totalMinutes += minutes;
    }
  }

  const secondsAsMinutes = Math.floor(
    totalRawSeconds / mergedOptions.secondsPerMinute,
  );
  const roundedTotalMinutes = Math.round(totalMinutes) + secondsAsMinutes;
  const finalHours = Math.floor(
    roundedTotalMinutes / mergedOptions.minutesPerHour,
  );
  const finalMinutes = roundedTotalMinutes % mergedOptions.minutesPerHour;
  const isValid = parts.length > 0;
  const formatted =
    isValid && roundedTotalMinutes === 0
      ? "0 hours"
      : formatEffortFromMinutes(roundedTotalMinutes, mergedOptions);

  return {
    input,
    totalMinutes: roundedTotalMinutes,
    hours: finalHours,
    minutes: finalMinutes,
    formatted,
    isValid,
    parts,
  };
};

export const formatEffortShorthand = (
  hours: number,
  minutes: number,
): string => {
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  return parts.join(" ") || "0m";
};

type EffortEntryContainer = {
  _id: string;
  effortActual: IEffortActual[];
  totalEffortActual: TotalEffortType;
};

export const calculateTotalEffort = (
  effortActualList: IEffortActual[],
): TotalEffortType => {
  const total = effortActualList.reduce(
    (acc, entry) => ({
      hours: acc.hours + (entry.effort?.hours || 0),
      minutes: acc.minutes + (entry.effort?.minutes || 0),
    }),
    { hours: 0, minutes: 0 },
  );

  // Normalize minutes to hours if >= 60
  if (total.minutes >= 60) {
    total.hours += Math.floor(total.minutes / 60);
    total.minutes = total.minutes % 60;
  }

  return total;
};

export const addOrUpdateEffortActualEntry = <T extends EffortEntryContainer>(
  myTicketsList: T[],
  ticketId: string,
  entry: IEffortActual | Partial<IEffortActual>,
  entryId?: string,
): T[] => {
  return myTicketsList.map((ticket) => {
    if (ticket._id !== ticketId) {
      return ticket;
    }

    const updatedTicket = {
      ...ticket,
      effortActual: [...ticket.effortActual],
    };

    if (entryId) {
      // Update existing entry
      const entryIndex = updatedTicket.effortActual.findIndex(
        (e) => e._id === entryId,
      );
      if (entryIndex !== -1) {
        updatedTicket.effortActual[entryIndex] = {
          ...updatedTicket.effortActual[entryIndex],
          ...(entry as Partial<IEffortActual>),
        };
      }
    } else {
      // Add new entry
      updatedTicket.effortActual.push(entry as IEffortActual);
    }

    // Recalculate total effort
    updatedTicket.totalEffortActual = calculateTotalEffort(
      updatedTicket.effortActual,
    );

    return updatedTicket;
  });
};

export const removeEffortActualEntry = <T extends EffortEntryContainer>(
  myTicketsList: T[],
  ticketId: string,
  effortId: string,
): T[] => {
  return myTicketsList.map((ticket) => {
    if (ticket._id !== ticketId) {
      return ticket;
    }

    const updatedEffortActual = ticket.effortActual.filter(
      (entry) => entry._id !== effortId,
    );

    return {
      ...ticket,
      effortActual: updatedEffortActual,
      totalEffortActual: calculateTotalEffort(updatedEffortActual),
    };
  });
};

export const parseContentDoc = (
  comment: string | undefined,
): JsonDocType | null => {
  if (!comment || typeof comment !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(comment) as JsonDocType;

    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Array.isArray(parsed.content)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const updateDescription = (description: ITicketDescription) => {
  if (!description) return description;

  const parsedComment = parseContentDoc(description.rawContent as string);

  if (!parsedComment || !parsedComment.content?.length) {
    return description;
  }

  const updatedRawContent = {
    ...parsedComment,
    content: updateContent(
      parsedComment.content,
      description.attachments || [],
    ),
  };

  return {
    ...description,
    rawContent: updatedRawContent,
  };
};

export const updateContent = (
  content: JSONContent[] = [],
  attachments: AttachmentType[] = [],
) => {
  return content.map((node: JSONContent) => {
    if (node.type === "image" && node.attrs?.imageId) {
      const attachment = attachments.find(
        (att: AttachmentType) => att.fileId === node.attrs?.imageId,
      );

      return {
        ...node,
        attrs: {
          ...node.attrs,
          src: attachment?.url || node.attrs?.src,
        },
      };
    }

    if (node.content) {
      return {
        ...node,
        content: updateContent(node.content, attachments),
      };
    }

    return node;
  });
};

function createDoc(text: string): JSONContent {
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        attrs: { textAlign: null },
        content: [
          {
            type: "text",
            text,
          },
        ],
      },
    ],
  };
}

export function addHeadingToDoc(
  doc: string | JSONContent,
  headingText: string,
  level: number = 4,
): JSONContent {
  if (!headingText?.trim()) {
    return typeof doc === "string" ? createDoc(doc) : doc;
  }

  const baseDoc: JSONContent = typeof doc === "string" ? createDoc(doc) : doc;

  const headingNode: JSONContent = {
    type: "heading",
    attrs: {
      level,
      textAlign: null,
    },
    content: [
      {
        type: "text",
        text: headingText,
      },
    ],
  };

  return {
    ...baseDoc,
    content: [headingNode, ...(baseDoc.content || [])],
  };
}
